import React, { useEffect, useMemo, useState, createElement } from 'react';
import { DashboardHeader } from './components/DashboardHeader';
import { InputCard } from './components/InputCard';
import { MainMetrics } from './components/MainMetrics';
import { CriticalThresholds } from './components/CriticalThresholds';
import { DeliveryFunnel } from './components/DeliveryFunnel';
import { TrafficLight } from './components/TrafficLight';
import { CTAButton } from './components/CTAButton';
import { CountrySelector } from './components/CountrySelector';
import { calculateMetrics, calculateArgentinaMetrics } from './utils/calculations';
import type { DashboardInputs, ArgentinaInputs, CountryCode } from './utils/calculations';
export function App() {
  useEffect(() => {
    document.title = 'TABLERO ESTRATÉGICO BY LUCAS TORRES';
    const setMeta = (name: string, content: string) => {
      let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', name);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };
    setMeta('description', 'Tablero estratégico de rentabilidad ecommerce para Colombia, México y Argentina.');
  }, []);
  const [country, setCountry] = useState<CountryCode>('AR');
  const [coInputs, setCoInputs] = useState<DashboardInputs>({
    price: 99900,
    productCost: 36000,
    fleteInicial: 19000,
    cancelledPct: 10,
    deliveredPct: 75,
    cpaIdeal: 15000
  });
  const [mxInputs, setMxInputs] = useState<DashboardInputs>({
    price: 1097,
    productCost: 435,
    fleteInicial: 200,
    cancelledPct: 10,
    deliveredPct: 65,
    cpaIdeal: 120
  });
  const [arInputs, setArInputs] = useState<ArgentinaInputs>({
    price: 89900,
    productCost: 36000,
    costoEnvio: 8000,
    cpaActual: 8000,
    mercadoPagoPct: 7,
    comisionCuotasPct: 16,
    clientesCuotasPct: 70,
    comisionTiendaPct: 3
  });
  const isAr = country === 'AR';
  const activeCodInputs = country === 'MX' ? mxInputs : coInputs;
  const setActiveCodInputs = country === 'MX' ? setMxInputs : setCoInputs;
  const codMetrics = useMemo(() => calculateMetrics(activeCodInputs), [activeCodInputs]);
  const arMetrics = useMemo(() => calculateArgentinaMetrics(arInputs), [arInputs]);
  const handleCodInputChange = (field: keyof DashboardInputs, value: number) => {
    setActiveCodInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleArInputChange = (field: keyof ArgentinaInputs, value: number) => {
    setArInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };
  return <div className="min-h-screen bg-dark text-white selection:bg-salmon/30 selection:text-white font-sans overflow-x-hidden" data-id="element-0">
      <div className="fixed inset-0 pointer-events-none z-0" data-id="element-1">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-salmon/5 blur-[120px]" data-id="element-2"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyber-green/5 blur-[120px]" data-id="element-3"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" data-id="element-4"></div>
      </div>

      <main className="relative z-10 container mx-auto px-4 py-12 max-w-6xl" data-id="element-5">
        <DashboardHeader country={country} data-id="element-6" />

        <CountrySelector selected={country} onChange={setCountry} data-id="element-7" />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-4 mb-6" data-id="element-8">
          <InputCard inputs={activeCodInputs} metrics={codMetrics} onChange={handleCodInputChange} arInputs={arInputs} arMetrics={arMetrics} onArChange={handleArInputChange} country={country} data-id="element-9" />
          <MainMetrics metrics={codMetrics} arMetrics={arMetrics} country={country} data-id="element-10" />
          <CriticalThresholds metrics={codMetrics} arMetrics={arMetrics} country={country} data-id="element-11" />
        </div>

        {!isAr && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6" data-id="element-12">
            <DeliveryFunnel metrics={codMetrics} data-id="element-13" />
            <TrafficLight metrics={codMetrics} country={country} data-id="element-14" />
          </div>}

        <CTAButton data-id="element-15" />
      </main>
    </div>;
}